/**
 * @module app.shared
 */

import { autoinject, bindingMode, customAttribute } from 'aurelia-framework';
import { TestIdService } from 'app/shared/services/test-id-service';
import { showInfoTip, hideInfoTip } from 'app/shared/elements/layout/ess-info-tip';
import { EnvironmentService } from 'app/shared/services/environment-service';

// used to track previously hovered element when the mouse leaves an element
let elementStack: Element[] = [];

@autoinject
@customAttribute('test-id', bindingMode.twoWay)
export class TestIdCustomAttribute {

    // value of the custom attribute
    public value: any;

    private showTips: boolean = false;
    private mouseEnter: EventListener | null = null;
    private mouseLeave: EventListener | null = null;

    constructor(private readonly element: Element, private readonly idService: TestIdService, private readonly env: EnvironmentService) {
        this.showTips = this.env.config?.testing?.enabled || false;
    }

    /**
     * Build the `test-id` from the value bound to this custom attribute
     * @param name
     */
    protected buildTestId(name: string): string {
        if(!name) { return ''; }
        return String(name);
    }

    /**
     * Lifecycle: Attached
     */
    protected attached(): void {
        const value = this.value;
        if(value) {
            this.idService.setId(this.element, this.buildTestId(value));
        }

        // add event listener
        if(this.showTips) {
            this.mouseEnter = e => this.onMouseEnter(<MouseEvent>e);
            this.mouseLeave = e => this.onMouseLeave(<MouseEvent>e);
            this.element.addEventListener('mouseenter', this.mouseEnter);
            this.element.addEventListener('mouseleave', this.mouseLeave);
        }
    }

    /**
     * Lifecycle: Detached
     */
    protected detached(): void {
        this.idService.clearId(this.element);

        // remove event listeners
        if(this.mouseEnter) {
            this.element.removeEventListener('mouseenter', this.mouseEnter);
        }
        if(this.mouseLeave) {
            this.element.removeEventListener('mouseleave', this.mouseLeave);
        }
    }

    /**
     * Change Handler for `value`
     * @param newValue
     * @param oldValue
     */
    protected valueChanged(newValue: any, oldValue: any): void {
        if(newValue !== oldValue) {
            this.idService.setId(this.element, this.buildTestId(newValue));
        }
    }

    /**
     * Handles `mouseenter` event
     * @param event
     */
    private onMouseEnter(event: MouseEvent): boolean {
        if(!event.ctrlKey || !event.shiftKey) { return true; }

        // remove highlight from all other elements
        for(const element of elementStack) {
            element.classList.remove('test-id-highlight');
        }
        // highlight this element
        this.element.classList.add('test-id-highlight');
        elementStack.push(this.element);

        // show the path to the user
        this.showElementPath(this.element);

        return false;
    }

    /**
     * Handles `mouseleave` event
     * @param event
     */
    private onMouseLeave(event: MouseEvent): boolean {
        if(!event.ctrlKey || !event.shiftKey) { return true; }

        // remove highlight from this element
        this.element.classList.remove('test-id-highlight');

        // remove this element from anywhere in the element stack
        elementStack = elementStack.filter(e => e != this.element);

        // find the most recently added item on the stack and show the path to the user
        if(elementStack.length > 0) {
            this.showElementPath(elementStack[elementStack.length - 1]);
        }
        else {
            this.showElementPath(null);
        }

        return false;
    }

    /**
     * Show an info-tip
     * @param element
     */
    private showElementPath(element: Element | null): void {
        if(element) {
            // show tip
            const path = this.idService.getIdPath(element).map(p => `"${p}"`).join(', ');
            const message = `(${path})`;
            showInfoTip('app', 'test-id', message, 'debug');
        }
        else {
            // hide tip
            hideInfoTip('app');
        }
    }

}
